import React, { useEffect, useMemo, useRef, useState } from 'react';

interface EnglishDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  containerClassName?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  style?: React.CSSProperties;
}

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toIsoDate(date) === value ? date : null;
}

function formatDate(value: string): string {
  const date = parseIsoDate(value);
  if (!date) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function parseDisplayDate(value: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  const iso = toIsoDate(date);
  return parseIsoDate(iso) && date.getDate() === Number(match[1]) ? iso : null;
}

export default function EnglishDatePicker({
  value,
  onChange,
  className = '',
  containerClassName = '',
  id,
  name,
  disabled = false,
  min,
  max,
  style,
}: EnglishDatePickerProps) {
  const selectedDate = parseIsoDate(value);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => formatDate(value));
  const [viewDate, setViewDate] = useState(() => selectedDate ?? new Date());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(formatDate(value));
    const next = parseIsoDate(value);
    if (next) setViewDate(next);
  }, [value]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const days = useMemo(() => {
    const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const offset = (firstDay.getDay() + 6) % 7;
    const lastDay = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    return Array.from({ length: offset + lastDay }, (_, index) => index < offset ? null : index - offset + 1);
  }, [viewDate]);

  const isAllowed = (iso: string) => (!min || iso >= min) && (!max || iso <= max);

  const selectDate = (day: number) => {
    const iso = toIsoDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), day));
    if (!isAllowed(iso)) return;
    onChange(iso);
    setOpen(false);
  };

  const commitInput = () => {
    if (!inputValue.trim()) {
      onChange('');
      return;
    }
    const iso = parseDisplayDate(inputValue);
    if (iso && isAllowed(iso)) {
      onChange(iso);
      return;
    }
    setInputValue(formatDate(value));
  };

  return (
    <div ref={rootRef} className={`relative w-full ${containerClassName}`}>
      <div className="relative">
        <input
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/YYYY"
          value={inputValue}
          disabled={disabled}
          onChange={(event) => setInputValue(event.target.value)}
          onBlur={commitInput}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitInput();
              setOpen(false);
            }
            if (event.key === 'Escape') setOpen(false);
          }}
          className={`w-full ${className}`}
          style={style}
        />
        <button
          type="button"
          aria-label="Open calendar"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-3 flex items-center justify-between">
            <button type="button" aria-label="Previous month" onClick={() => setViewDate((date) => new Date(date.getFullYear(), date.getMonth() - 1, 1))} className="rounded p-1 text-gray-600 hover:bg-gray-100">‹</button>
            <span className="text-sm font-semibold text-gray-900">{months[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
            <button type="button" aria-label="Next month" onClick={() => setViewDate((date) => new Date(date.getFullYear(), date.getMonth() + 1, 1))} className="rounded p-1 text-gray-600 hover:bg-gray-100">›</button>
          </div>
          <div className="grid grid-cols-7 text-center text-xs">
            {weekdays.map((weekday) => <span key={weekday} className="py-1 font-medium text-gray-500">{weekday}</span>)}
            {days.map((day, index) => {
              if (!day) return <span key={`empty-${index}`} />;
              const iso = toIsoDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), day));
              const active = iso === value;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={!isAllowed(iso)}
                  onClick={() => selectDate(day)}
                  className={`m-0.5 rounded py-1.5 transition-colors disabled:cursor-not-allowed disabled:text-gray-300 ${active ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-indigo-50'}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between border-t border-gray-100 pt-2">
            <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-xs font-medium text-gray-600 hover:text-gray-900">Clear</button>
            <button type="button" onClick={() => { const today = toIsoDate(new Date()); if (isAllowed(today)) onChange(today); setOpen(false); }} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">Today</button>
          </div>
        </div>
      )}
    </div>
  );
}
